<template>
  <div
    class="input"
    :class="{
      error: vErrors.length > 0 || isError,
      [`input--${mode}`]: true,
    }"
  >
    <div class="input__title" v-if="windowWidth > 999">
      {{ translatedTitle }}
    </div>
    <div class="input__title" @click="toggleTitle" v-else>
      {{ truncatedTitle ? truncatedTitle : translatedTitle }}
    </div>
    <div class="input__el-wrapper" :class="{ concentration: concentration }">
      <div
        class="expand-icon"
        v-if="expandable || shouldUseLimitsModal"
        @click="expandArea"
      >
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M0 5C0 2.23858 2.23858 0 5 0H20V15C20 17.7614 17.7614 20 15 20H12.5C11.9477 20 11.5 19.5523 11.5 19C11.5 18.4477 11.9477 18 12.5 18H15C16.6569 18 18 16.6569 18 15V2H5C3.34315 2 2 3.34315 2 5V7.5C2 8.05228 1.55228 8.5 1 8.5C0.447715 8.5 0 8.05228 0 7.5V5Z"
            fill="#64748B"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M0 12.5C0 11.3954 0.895431 10.5 2 10.5H7.5C8.60457 10.5 9.5 11.3954 9.5 12.5V18C9.5 19.1046 8.60457 20 7.5 20H2C0.89543 20 0 19.1046 0 18V12.5ZM7.5 12.5H2V18H7.5V12.5Z"
            fill="#64748B"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M9.5 5C9.5 4.44772 9.94772 4 10.5 4H16V9.5C16 10.0523 15.5523 10.5 15 10.5C14.4477 10.5 14 10.0523 14 9.5V6H10.5C9.94772 6 9.5 5.55228 9.5 5Z"
            fill="#64748B"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M14.7071 5.29304C15.0976 5.68357 15.0976 6.31673 14.7071 6.70726L11.7071 9.70726C11.3166 10.0978 10.6834 10.0978 10.2929 9.70726C9.90237 9.31673 9.90237 8.68357 10.2929 8.29304L13.2929 5.29304C13.6834 4.90252 14.3166 4.90252 14.7071 5.29304Z"
            fill="#64748B"
          />
        </svg>
      </div>
      <textarea
        class="input__el"
        :title="title"
        :disabled="disabled"
        v-model="text"
        @input="processInput"
        @focus="processFocus"
        @blur="processBlur"
        @keypress.enter="$emit('enter')"
        v-if="!mask"
        ref="inputEl"
        :placeholder="translatedPlaceholder"
        :readonly="shouldUseLimitsModal"
      />
      <textarea
        class="input__el"
        :title="title"
        :disabled="disabled"
        v-model="text"
        @input="processInput"
        @focus="processFocus"
        @blur="processBlur"
        @keypress.enter="$emit('enter')"
        v-mask="mask"
        v-else
        ref="inputEl"
        :placeholder="translatedPlaceholder"
      />
      <div class="input__copy" v-if="canCopy" @click="copyToClipboard">
        <svg
          width="31"
          height="30"
          viewBox="0 0 31 30"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <ellipse cx="15.8825" cy="15" rx="15.0519" ry="15" fill="#DBE7FD" />
          <path
            d="M10.9928 9.97028L10.9907 11.5625V19.4407C10.9907 20.7868 12.0858 21.8782 13.4366 21.8782L19.9212 21.8784C19.6887 22.5337 19.0615 23.0032 18.3243 23.0032H13.4366C11.4623 23.0032 9.86182 21.4082 9.86182 19.4407V11.5625C9.86182 10.827 10.334 10.2013 10.9928 9.97028ZM20.21 8C21.1452 8 21.9033 8.75552 21.9033 9.6875V19.4375C21.9033 20.3695 21.1452 21.125 20.21 21.125H13.4366C12.5014 21.125 11.7433 20.3695 11.7433 19.4375V9.6875C11.7433 8.75552 12.5014 8 13.4366 8H20.21ZM20.21 9.125H13.4366C13.1249 9.125 12.8722 9.37684 12.8722 9.6875V19.4375C12.8722 19.7482 13.1249 20 13.4366 20H20.21C20.5217 20 20.7744 19.7482 20.7744 19.4375V9.6875C20.7744 9.37684 20.5217 9.125 20.21 9.125Z"
            fill="#64748B"
          />
        </svg>
      </div>
    </div>

    <div class="input__hint" v-if="hint">
      {{ translatedHint }}
    </div>
    <div class="input__errors" v-if="vErrors.length > 0">
      <div class="input__error">{{ vErrors[0].$message }}</div>
    </div>
    <text-area-expanded-popup ref="expandedPopup"></text-area-expanded-popup>

    <limits-modal v-if="shouldUseLimitsModal" ref="limitsModal" />
  </div>
</template>

<script>
import TextAreaExpandedPopup from '@vue-app/components/base/input/textarea/TextAreaExpandedPopup.vue'
import LimitsModal from '@vue-app/components/base/input/textarea/LimitsModal.vue'

export default {
  name: 'TextAreaInput',
  components: { TextAreaExpandedPopup, LimitsModal },
  model: {
    prop: 'value',
    event: 'update',
  },
  props: {
    existLimitItems: {
      type: [Array, Object],
      default() {
        return []
      },
    },
    partnerId: {
      type: [Number, String],
      required: false,
    },
    title: {
      type: [String, Number, Object],
      default: '',
    },
    placeholder: {
      type: [String, Object],
      default: '',
    },
    icon: {
      type: String,
      default: null,
    },
    value: [String, Number],
    hint: {
      type: [String, Object, Array],
      default: null,
    },
    closeButton: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'text',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    vErrors: {
      type: Array,
      default() {
        return []
      },
    },
    isError: {
      type: Boolean,
      default: false,
    },
    mask: {
      type: [String, Object, Function, Array],
      default: null,
    },
    canCopy: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'base',
    },
    shouldTranslateTitle: {
      type: Boolean,
      default: false,
    },
    localizedTitle: {
      type: Boolean,
      default: false,
    },
    concentration: {
      type: Boolean,
      default: false,
    },
    expandable: {
      type: Boolean,
      default: false,
    },

    operationType: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      text: '',
      isFocused: false,
      showFullTitle: false,
      windowWidth: window.innerWidth,
    }
  },
  created() {
    this.text = this.value === 0 ? this.value : this.value || ''
  },
  mounted() {
    this.$emit('canFocus', this)
    window.addEventListener('resize', this.handleResize)
    this.resizeTextarea()
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize)
  },
  computed: {
    locale() {
      let lang = this.$i18n?.locale ?? 'en'
      return lang === 'ua' ? 'uk' : lang
    },
    translatedTitle() {
      if (this.shouldTranslateTitle) {
        return this.$t(this.title)
      }

      if (this.localizedTitle && typeof this.title === 'object') {
        return this.title[this.locale] ?? this.title['en'] ?? ''
      }

      return this.title
    },
    translatedPlaceholder() {
      if (this.shouldTranslateTitle) {
        return this.$t(this.placeholder)
      }

      if (this.localizedTitle) {
        return this.placeholder[this.locale] ?? this.placeholder['en'] ?? ''
      }

      return this.placeholder
    },
    translatedHint() {
      if (this.shouldTranslateTitle) {
        return this.$t(this.hint)
      }

      if (this.localizedTitle && typeof this.hint === 'object') {
        return this.hint[this.locale] ?? this.hint['en'] ?? ''
      }

      return this.hint
    },

    truncatedTitle() {
      return this.showFullTitle
        ? this.$t(this.title).uk
        : this.truncateTitle(this.$t(this.title).uk, 20)
    },
    shouldUseLimitsModal() {
      return (
        typeof this.title === 'object' &&
        this.title.uk &&
        this.title.uk.includes('Встановлені обмеження на надання послуги')
      )
    },
  },
  watch: {
    value: function () {
      this.text = this.value ?? ''
      this.resizeTextarea()
    },
  },
  methods: {
    toggleTitle() {
      this.showFullTitle = !this.showFullTitle
    },

    truncateTitle(text, length) {
      if (text !== undefined) {
        if (text.length <= length) {
          return text
        } else {
          return text.substring(0, length) + '...'
        }
      } else {
        return text
      }
    },

    checkAnimation(e) {
      if (e.animationName === 'onAutoFillStart') {
        this.text = this.text + ' '
        this.autofilled = true
      } else if (e.animationName === 'onAutoFillCancel') {
        this.text = this.text + ' '
        this.autofilled = false
      }
    },

    processFocus() {
      this.$emit('textInputFocus')
      this.isFocused = true
    },
    processBlur() {
      this.$emit('textInputBlur')
      this.isFocused = false
    },
    processInput() {
      this.$emit('update:value', this.text)
      this.resizeTextarea()
    },

    resizeTextarea() {
      const textarea = this.$refs.inputEl
      if (textarea) {
        textarea.style.height = 'auto'
        const scrollHeight = textarea.scrollHeight
        const maxHeight = 400

        if (scrollHeight > maxHeight) {
          textarea.style.height = `${maxHeight}px`
          textarea.style.overflowY = 'scroll'
        } else {
          textarea.style.height = `${scrollHeight}px`
          textarea.style.overflowY = 'hidden'
        }
      }
    },

    copyToClipboard() {
      if (!navigator.clipboard) {
        return
      }

      navigator.clipboard.writeText(this.text).then(
        () => {
          this.$emit('copied')
        },
        (err) => {
          console.error('Async: Could not copy text: ', err)
        }
      )
    },
    focus() {
      this.$refs.inputEl.focus()
    },
    async expandArea() {
      if (this.shouldUseLimitsModal) {
        const response = await this.$refs.limitsModal.open(
          this.translatedTitle,
          this.translatedHint,
          this.existLimitItems,
          this.operationType,
          this.partnerId
        )
        if (!response) {
          return
        }

        this.$emit('limitsChange', response)

        const summary = response
          .map((item) => {
            if (item.act)
              return `${
                item.name || this.$t('limits-modal.select.placeholder')
              } - ${item.val}`
          })
          .join('\n')

        this.text = summary
        this.processInput()

        return
      }

      const _response = await this.$refs.expandedPopup.open(
        this.translatedTitle,
        this.text
      )

      if (!_response) {
        return
      }

      this.text = _response
      this.processInput()
    },
  },
  emits: [
    'enter',
    'canFocus',
    'update',
    'limitsChange',
    'textInputFocus',
    'textInputBlur',
    'copied',
    'update:value',
    ,
    'enter',
    ,
    'update:value',
  ],
}
</script>

<style lang="scss" scoped>
.input {
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;
  &__copy {
    position: absolute;
    top: 20px;
    right: 20px;

    width: 30px;
    height: 30px;

    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }

    svg {
      width: 100%;
      height: 100%;
    }
  }

  &__title {
    font-weight: 400;
    font-size: 12px;
    line-height: 15px;
    color: #465a8b;
    padding: 2px 10px;
    position: absolute;
    top: 0;
    left: 15px;
    max-width: calc(100% - 50px);

    transform: translateY(-50%);
    background-color: #fff;
    z-index: 1;
  }

  &--small & {
    &__el {
      height: 88px;
    }
  }

  &__el {
    width: 100%;
    height: 155px;
    resize: none;
    border: 1px solid #e0e6f2;
    background-color: transparent;
    border-radius: 6px;
    padding: 24px 15px 24px 21px;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: #585f6d;
    outline: none;

    &-wrapper {
      position: relative;

      &:hover {
        .expand-icon {
          opacity: 1;
        }
      }
    }

    &:focus,
    &:active {
      border-color: #64748B;
    }

    &:disabled {
      border-color: #eee;
      color: #d0d9ea;
    }
  }

  &__hint {
    margin-top: 4px;
    font-weight: 500;
    font-size: 10px;
    line-height: 14px;
    color: #b9c4d8;
  }

  .expand-icon {
    position: absolute;
    top: 6px;
    right: 6px;
    opacity: 0;
    transition: opacity 0.2s linear;
    cursor: pointer;

    &:hover {
      opacity: 0.8;
    }
  }
}
</style>

<style>
:-webkit-autofill {
  animation-name: onAutoFillStart;
}
:not(:-webkit-autofill) {
  animation-name: onAutoFillCancel;
}
@keyframes onAutoFillStart {
  from {
  }
  to {
  }
}
@keyframes onAutoFillCancel {
  from {
  }
  to {
  }
}
</style>
